import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader } from 'rsuite';
import { refreshSession } from '../utils/config/apiClient';
import {
  GetToken,
  forceLogout,
  homeForRole,
  isSessionExpired,
  readSession,
  rememberReturnPath,
} from '../utils/helper/helper';

/**
 * Guards one role-scoped subtree of App.js.
 *
 * FE-01 (#103): this used to render the subtree for anyone with a truthy `role` in localStorage.
 * It ignored the `role` prop App.js passed it and never looked at the token, so any signed-in
 * user reached every other role's screens by typing the URL.
 *
 * THE ROLE COMES FROM THE ACCESS TOKEN, not from the `role` localStorage key. That key is still
 * written at sign-in and still drives the sidebar and top bar, but only as a display hint.
 * Neither is a security boundary - see readSession() - the server's [CustomAuthorize] is.
 *
 * AN EXPIRED ACCESS TOKEN IS RENEWED HERE, NEVER TREATED AS SIGNED OUT. The access token lives
 * 20 minutes and the refresh token outlives it; redirecting on a past `exp` is the WEB-P1-161
 * bug TopNavbar used to have, which signed out exactly the users apiClient.js was about to
 * renew. Only a refresh the server refuses ends the session.
 *
 * A signed-in user of the wrong role is sent to their own dashboard rather than to /Login: the
 * session is fine, and the sign-in form is the wrong answer to a mistyped URL.
 *
 * #199: a visitor with no session who asked for a page in the return allow-list (Account Settings,
 * the account-deletion link given to Google Play) is brought back to it after signing in - see
 * rememberReturnPath. A stale session ends in forceLogout, which remembers the page the same way.
 *
 * @param {object} props
 * @param {string[]} props.allowedRoles The role names, as the server spells them, admitted here.
 */
const PrivateRoute = ({ allowedRoles = [] }) => {
  const location = useLocation();
  // The token this guard renewed itself. It is accepted even if its `exp` still reads as past:
  // with the device clock running fast every fresh token looks expired, and the loader below
  // would never go away. The server has just issued it, so it is live by definition.
  const [trustedToken, setTrustedToken] = useState(null);

  const session = readSession();
  const needsRenewal =
    session !== null && isSessionExpired(session) && GetToken() !== trustedToken;

  useEffect(() => {
    if (!needsRenewal) return;

    let active = true;
    // Single-flight: if a request's 401 has already started a refresh, this joins it.
    refreshSession().then((renewed) => {
      if (!active) return;
      if (renewed) {
        setTrustedToken(GetToken());
      } else {
        forceLogout('Your session has ended. Please sign in again.');
      }
    });

    return () => {
      active = false;
    };
  }, [needsRenewal]);

  if (!session) {
    rememberReturnPath(location.pathname);
    return <Navigate to="/Login" replace />;
  }

  if (needsRenewal) return <Loader center />;

  if (!allowedRoles.includes(session.role)) {
    return <Navigate to={homeForRole(session.role) ?? '/Login'} replace />;
  }

  return <Outlet />;
};

export default PrivateRoute;
